// src/ChangePasswordPage.js - シンプル版
import React, { useState } from "react";
import { supabase } from "./supabase";
import { RefreshCw, Lock, Eye, EyeOff, CheckCircle, AlertCircle } from "lucide-react";

function ChangePasswordPage() {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      setError("パスワードが一致しません");
      return;
    }
    
    // パスワード要件チェック（強化版）
    if (newPassword.length < 8) {
      setError("パスワードは8文字以上で設定してください");
      return;
    }
    
    if (!/[A-Z]/.test(newPassword)) {
      setError("パスワードには大文字 (A-Z) を含めてください");
      return;
    }
    
    if (!/[a-z]/.test(newPassword)) {
      setError("パスワードには小文字 (a-z) を含めてください");
      return;
    }
    
    if (!/[0-9]/.test(newPassword)) {
      setError("パスワードには数字 (0-9) を含めてください");
      return;
    }
    
    setLoading(true);
    setError("");
    
    try {
      const token = localStorage.getItem('supabase_token');
      console.log("📋 localStorage確認:");
      console.log("  - supabase_token:", token ? `存在 (${token.substring(0, 50)}...)` : "null");
      console.log("  - newPassword:", newPassword ? `存在 (長さ: ${newPassword.length})` : "null");
      
      if (!token) {
        throw new Error('認証トークンが見つかりません。再ログインしてください。');
      }
      
      console.log("🔐 パスワード変更リクエスト送信中...");
      console.log("📦 送信データ:", { 
        hasToken: !!token, 
        tokenLength: token?.length,
        hasNewPassword: !!newPassword,
        newPasswordLength: newPassword?.length 
      });
      
      // supabase.functions.invoke()を使う（本物のSupabase AuthのJWTがあるので動作する）
      const { data, error } = await supabase.functions.invoke('change-password', {
        body: {
          token: token,
          newPassword: newPassword
        }
      });
      
      console.log("📥 レスポンス:", { data, error });
      
      if (error) {
        throw new Error(error.message || 'パスワード変更に失敗しました');
      }
      
      if (!data.success) {
        throw new Error(data.message || 'パスワード変更に失敗しました');
      }
      
      console.log("✅ パスワード変更成功");
      
      // require_password_changeフラグを削除
      localStorage.removeItem('require_password_change');
      
      alert("パスワードが変更されました");
      window.location.href = '/home';
      
    } catch (error) {
      console.error("❌ パスワード変更エラー:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* 背景グラデーション（ログインページと同じ） */}
      <div style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        background: "linear-gradient(135deg, #1a365d 0%, #2563eb 100%)",
        overflow: "auto",
      }}>
        {/* 装飾的な背景パターン */}
        <div style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          opacity: 0.12,
          background: `
            radial-gradient(circle at 20% 30%, rgba(255,255,255,0.25) 0%, transparent 50%),
            radial-gradient(circle at 80% 70%, rgba(255,255,255,0.15) 0%, transparent 50%),
            radial-gradient(circle at 40% 80%, rgba(255,255,255,0.2) 0%, transparent 50%)
          `,
          animation: "float 20s ease-in-out infinite",
        }}></div>

        {/* メインコンテンツ */}
        <div style={{
          position: "relative",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "3rem 1.5rem",
          boxSizing: "border-box",
        }}>

          {/* タイトルエリア */}
          <div style={{
            textAlign: "center",
            marginBottom: "2rem",
            animation: "fadeInDown 0.8s ease-out",
          }}>
            <div style={{
              width: "100px",
              height: "100px",
              borderRadius: "50%",
              background: "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 1.5rem",
              boxShadow: "0 20px 60px rgba(37, 99, 235, 0.4)",
              animation: "pulse 2s ease-in-out infinite",
            }}>
              <Lock size={50} color="white" />
            </div>
            <h2 style={{ 
              margin: 0, 
              fontSize: "clamp(1.75rem, 5vw, 2.25rem)", 
              fontWeight: "800", 
              color: "white",
              textShadow: "0 4px 20px rgba(0,0,0,0.2)",
              letterSpacing: "-0.5px",
            }}>
              パスワード変更
            </h2>
            <p style={{ 
              margin: "0.75rem 0 0 0", 
              fontSize: "clamp(0.95rem, 2.5vw, 1.1rem)", 
              color: "rgba(255, 255, 255, 0.9)",
              fontWeight: "500",
              textShadow: "0 2px 10px rgba(0,0,0,0.15)",
            }}>
              新しいパスワードを設定してください
            </p>
          </div>

          {/* メインカード */}
          <div style={{
            backgroundColor: "rgba(255, 255, 255, 0.98)",
            borderRadius: "24px",
            padding: "2.5rem 2rem",
            maxWidth: "480px",
            width: "100%",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255,255,255,0.1)",
            backdropFilter: "blur(20px)",
            animation: "fadeInUp 0.8s ease-out 0.2s backwards",
          }}>
            
            {/* エラー表示 */}
            {error && (
              <div style={{
                padding: "1rem 1.25rem",
                background: "rgba(254, 226, 226, 0.95)",
                border: "2px solid #f87171",
                borderRadius: "12px",
                marginBottom: "1.5rem",
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                color: "#dc2626",
                animation: "shake 0.5s ease-in-out",
              }}>
                <AlertCircle size={20} />
                <span style={{ fontSize: "0.95rem", fontWeight: "600" }}>{error}</span>
              </div>
            )}

            <form onSubmit={handleChangePassword}>
              {/* 新しいパスワード */}
              <div style={{ marginBottom: "1.5rem" }}>
                <label style={{
                  display: "block",
                  fontSize: "0.9rem",
                  fontWeight: "700",
                  color: "#1f2937",
                  marginBottom: "0.6rem",
                  letterSpacing: "0.3px",
                }}>
                  新しいパスワード
                </label>
                <div style={{ position: "relative" }}>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="例: MyPassword123"
                    required
                    style={{
                      width: "100%",
                      padding: "1rem 3rem 1rem 1rem",
                      fontSize: "1rem",
                      border: "2px solid #e5e7eb",
                      borderRadius: "12px",
                      outline: "none",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      boxSizing: "border-box",
                      fontFamily: "inherit",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#2563eb";
                      e.target.style.boxShadow = "0 0 0 4px rgba(37, 99, 235, 0.1)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#e5e7eb";
                      e.target.style.boxShadow = "none";
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: "absolute",
                      right: "14px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: "6px",
                      display: "flex",
                      alignItems: "center",
                      color: "#6b7280",
                      transition: "color 0.2s",
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = "#2563eb"}
                    onMouseLeave={(e) => e.currentTarget.style.color = "#6b7280"}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {/* パスワード確認 */}
              <div style={{ marginBottom: "2rem" }}>
                <label style={{
                  display: "block",
                  fontSize: "0.9rem",
                  fontWeight: "700",
                  color: "#1f2937",
                  marginBottom: "0.6rem",
                  letterSpacing: "0.3px",
                }}>
                  パスワード確認
                </label>
                <div style={{ position: "relative" }}>
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="もう一度入力"
                    required
                    style={{
                      width: "100%",
                      padding: "1rem 3rem 1rem 1rem",
                      fontSize: "1rem",
                      border: "2px solid #e5e7eb",
                      borderRadius: "12px",
                      outline: "none",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      boxSizing: "border-box",
                      fontFamily: "inherit",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#2563eb";
                      e.target.style.boxShadow = "0 0 0 4px rgba(37, 99, 235, 0.1)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#e5e7eb";
                      e.target.style.boxShadow = "none";
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    style={{
                      position: "absolute",
                      right: "14px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: "6px",
                      display: "flex",
                      alignItems: "center",
                      color: "#6b7280",
                      transition: "color 0.2s",
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = "#2563eb"}
                    onMouseLeave={(e) => e.currentTarget.style.color = "#6b7280"}
                  >
                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {/* 変更ボタン */}
              <button
                type="submit"
                disabled={loading}
                style={{
                  width: "100%",
                  padding: "1.1rem 2rem",
                  background: loading 
                    ? "linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)"
                    : "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)",
                  color: "white",
                  border: "none",
                  borderRadius: "12px",
                  fontSize: "1.1rem",
                  fontWeight: "700",
                  cursor: loading ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.75rem",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  boxShadow: loading 
                    ? "none" 
                    : "0 10px 30px rgba(37, 99, 235, 0.4)",
                  letterSpacing: "0.5px",
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 15px 40px rgba(37, 99, 235, 0.5)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 10px 30px rgba(37, 99, 235, 0.4)";
                  }
                }}
              >
                {loading ? (
                  <>
                    <div style={{
                      display: "inline-block",
                      width: "18px",
                      height: "18px",
                      border: "3px solid rgba(255,255,255,0.3)",
                      borderTop: "3px solid white",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                    }}></div>
                    変更中...
                  </>
                ) : (
                  <>
                    <RefreshCw size={20} />
                    パスワード変更
                  </>
                )}
              </button>
            </form>

            {/* パスワード要件 */}
            <div style={{
              marginTop: "1.5rem",
              padding: "1.25rem",
              background: "linear-gradient(135deg, rgba(37, 99, 235, 0.05) 0%, rgba(30, 64, 175, 0.05) 100%)",
              borderRadius: "12px",
              border: "2px solid rgba(37, 99, 235, 0.1)",
            }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                marginBottom: "0.75rem",
                color: "#1e40af",
                fontWeight: "700",
                fontSize: "0.9rem",
              }}>
                <CheckCircle size={18} />
                パスワード要件
              </div>
              <div style={{
                fontSize: "0.85rem",
                color: "#475569",
                lineHeight: "1.8",
                paddingLeft: "1.75rem",
              }}>
                • 8文字以上<br />
                • 大文字を含む (A-Z)<br />
                • 小文字を含む (a-z)<br />
                • 数字を含む (0-9)
              </div>
            </div>
          </div>

          {/* フッター */}
          <div style={{
            marginTop: "2rem",
            fontSize: "0.85rem",
            color: "rgba(255, 255, 255, 0.8)",
            textAlign: "center",
            animation: "fadeIn 0.8s ease-out 0.4s backwards",
          }}>
            セキュリティのため、推測されにくいパスワードを設定してください
          </div>
        </div>
      </div>

      {/* アニメーション用スタイル */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @keyframes fadeInDown {
            from {
              opacity: 0;
              transform: translateY(-30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
          
          @keyframes shake {
            0%, 100% { transform: translateX(0); }
            25% { transform: translateX(-10px); }
            75% { transform: translateX(10px); }
          }
          
          @keyframes float {
            0%, 100% {
              transform: translate(0, 0) scale(1);
            }
            33% {
              transform: translate(30px, -30px) scale(1.1);
            }
            66% {
              transform: translate(-20px, 20px) scale(0.9);
            }
          }
          
          @keyframes pulse {
            0%, 100% {
              transform: scale(1);
              box-shadow: 0 20px 60px rgba(37, 99, 235, 0.4);
            }
            50% {
              transform: scale(1.05);
              box-shadow: 0 25px 70px rgba(37, 99, 235, 0.6);
            }
          }
          
          ::-webkit-scrollbar {
            width: 10px;
          }
          
          ::-webkit-scrollbar-track {
            background: rgba(255,255,255,0.1);
            border-radius: 10px;
          }
          
          ::-webkit-scrollbar-thumb {
            background: rgba(255,255,255,0.3);
            border-radius: 10px;
          }
          
          ::-webkit-scrollbar-thumb:hover {
            background: rgba(255,255,255,0.5);
          }
        `}
      </style>
    </>
  );
}

export default ChangePasswordPage;